// Calculation rules revisions — what keeps a filed report filed.
//
// Results are never stored; every screen and every reprint recalculates from
// the saved inputs. That is fine until an engine changes, at which point an
// already-completed report would silently print different numbers the next
// time it was opened (context/nec-2026-assessment.md, Gap C). The fix is the
// same one code_edition uses: completion freezes the rules a project was
// calculated under, and every engine reads that frozen revision instead of
// the newest one.
//
// Adding a revision: bump CURRENT_CALC_RULES, branch the engine on
// `calcRulesFor(project) >= N`, and leave every lower revision's arithmetic
// exactly as it was. Never edit what an existing revision computes.
//
//   1 — everything shipped up to 2026-10-12. Projects completed before the
//       column existed are revision 1 by definition (migration backfill).
//   2 — 2026 edition, measured path: new load that passes the season filter
//       takes the Table 120.83 percentages (measured22087.ts,
//       newLoadFactorsApply). Nothing changes under 2020 or 2023.
//   3 — 2026 edition, measured path: removed load that runs in the peak's
//       season is subtracted at 40% of its rating, after the 125%
//       (removedLoadCreditApplies). Removed load is listed, never credited,
//       under 2020/2023 and under revisions 1–2.
//   4 — 2026 edition, both paths: a new heat pump counts as central electric
//       resistance space heating (80%) unless its rating is stated to exclude
//       backup strip heat (loadRoles.ts countsAsCentralResistanceHeat).
//   5 — measured path, every edition: contractor-approved exclusions of
//       readings the suspect-peak checks flagged leave the readings in
//       effect (peakExclusions.ts readingsInEffect). Below 5 every reading
//       counts, whatever is stored.

export const CURRENT_CALC_RULES = 5;

type RulesSource = {
  calc_rules_version?: number | null;
  status?: string | null;
  completed_at?: string | null;
};

/**
 * The rules revision a project calculates under right now.
 *
 * Stored value first — the revision frozen at completion. An unstamped
 * project that has ever been completed was completed by code that predates
 * the stamp, so it is revision 1: the migration backfills those rows, and
 * this fallback covers any completed while the column existed but the old
 * client was still serving. Everything else follows the current rules.
 */
export function calcRulesFor(project: RulesSource): number {
  if (project.calc_rules_version != null) return project.calc_rules_version;
  if (project.status === 'completed' || project.completed_at) return 1;
  return CURRENT_CALC_RULES;
}
